import React, { useState, useEffect } from 'react'
import { Typography } from '~/components/_atoms'
import Rating from '~/components/_atoms/Rating'
import { useCandidateChat } from '../../CandidateChatContext'

/**
 * ChatEndedSurvey component - displays the survey form for rating the chat
 * Uses uncontrolled inputs with refs for form submission
 *
 * @param {Object} props
 * @param {string} props.title - Survey title text
 * @param {Object} props.ratingRef - Ref for rating input
 * @param {Object} props.tagsRef - Ref for tags input
 * @param {Object} props.feedbackRef - Ref for feedback textarea
 * @param {Function} props.onInputChange - Callback when any input changes
 */
const ChatEndedSurvey = ({
  title,
  ratingRef,
  tagsRef,
  feedbackRef,
  onInputChange,
}) => {
  const { getDarkModeClasses } = useCandidateChat()
  const [selectedRating, setSelectedRating] = useState(0)
  const [selectedTags, setSelectedTags] = useState([])
  const feedbackTags = [
    'Easy to understand',
    'Clear directions',
    'Nice',
    'Throrough',
    'Fast Replies',
    'Knowledgable',
    'Quick service',
    'Patient',
    'Professional',
  ]

  // Initialize hidden input values on mount
  useEffect(() => {
    if (ratingRef.current) {
      ratingRef.current.value = selectedRating
    }
    if (tagsRef.current) {
      tagsRef.current.value = selectedTags.join(',')
    }
  }, [ratingRef, selectedRating, selectedTags, tagsRef])

  const handleRatingChange = (rating) => {
    setSelectedRating(rating)
    // Update the hidden input value
    if (ratingRef.current) {
      ratingRef.current.value = rating
    }
    onInputChange()
  }

  const handleTagToggle = (tag) => {
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag]
    setSelectedTags(newTags)
    // Update the hidden input value
    if (tagsRef.current) {
      tagsRef.current.value = newTags.join(',')
    }
    onInputChange()
  }

  const handleFeedbackChange = (e) => {
    if (feedbackRef.current) {
      feedbackRef.current.value = e.target.value
    }
    onInputChange()
  }

  return (
    <div className="chat-ended-survey space-y-6 overflow-y-auto p-4">
      {/* Hidden form fields for form submission */}
      <input
        ref={ratingRef}
        type="hidden"
        name="rating"
        value={selectedRating}
      />
      <input
        ref={tagsRef}
        type="hidden"
        name="tags"
        value={selectedTags.join(',')}
      />

      {/* Survey Title */}
      {title && (
        <div className="chat-ended-survey-title text-center">
          <Typography
            variant="body"
            className={`font-roboto text-sm ${getDarkModeClasses('text-cod-gray-700', 'text-gray-300')}`}
          >
            {title}
          </Typography>
        </div>
      )}

      {/* Rating Section */}
      <div className="chat-ended-rating-section space-y-6 text-center">
        <Typography
          variant="body"
          className={`font-roboto font-medium ${getDarkModeClasses('text-cod-gray-950', 'text-white')}`}
        >
          Please let us know how helpful this chat was!
        </Typography>
        <div className="flex justify-center">
          <Rating
            value={selectedRating}
            onChange={handleRatingChange}
            size="lg"
            className="justify-center"
          />
        </div>
      </div>

      {/* Tag Selection */}
      <div className="chat-ended-tags-section space-y-6">
        <Typography
          variant="body"
          className={`text-center font-roboto font-medium ${getDarkModeClasses('text-cod-gray-950', 'text-white')}`}
        >
          Select most applicable:
        </Typography>
        <div className="flex flex-wrap justify-center gap-3">
          {feedbackTags.map((tag) => (
            <button
              key={tag}
              type="button"
              className={`rounded-full px-3 py-2 font-roboto text-sm transition-colors ${
                selectedTags.includes(tag)
                  ? 'bg-intoo-green-medium text-white'
                  : getDarkModeClasses(
                      'bg-cod-gray-100 text-cod-gray-950 hover:bg-cod-gray-200',
                      'bg-gray-600 text-white hover:bg-gray-500',
                    )
              }`}
              onClick={() => handleTagToggle(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Feedback Section */}
      <div className="chat-ended-feedback-section space-y-4">
        <Typography
          variant="body"
          className={`text-center font-roboto font-medium ${getDarkModeClasses('text-cod-gray-950', 'text-white')}`}
        >
          Please leave any additional feedback here.
        </Typography>
        <textarea
          ref={feedbackRef}
          name="feedback"
          className={`h-48 w-full resize-none rounded-xl border p-4 font-roboto text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-intoo-green-medium ${getDarkModeClasses(
            'border-cod-gray-300 bg-intoo-haze text-cod-gray-700 placeholder-cod-gray-500',
            'border-gray-600 bg-gray-700 text-white placeholder-gray-400',
          )}`}
          placeholder="Share your thoughts..."
          onChange={handleFeedbackChange}
          autoComplete="off"
          spellCheck="false"
        />
      </div>
    </div>
  )
}

export default ChatEndedSurvey
